import React, { useState } from "react";
import MusicNoteSelector from "./MusicNoteSelector";
import { COLORS } from "../utils/constants";
import { Menu, MenuItem, Button } from "@mui/material";

/**
 * KeySelector component for selecting musical keys
 * Combines MusicNoteSelector with hierarchical Mode selection
 * Displays keys in the format C# Major, F♭宫调, etc.
 */
const KeySelector = ({ value, onChange }) => {
  // State for the mode menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState({});
  const [openSubmenus, setOpenSubmenus] = useState({});
  
  // Hierarchical mode data structure
  const modeItems = [
    { label: "Major (大调)", value: "Major" },
    { label: "Minor (小调)", value: "Minor" },
    {
      label: "Church Modes (教会调式)",
      children: [
        { label: "Ionian (伊奥尼亚)", value: "Ionian" },
        { label: "Dorian (多利亚)", value: "Dorian" },
        { label: "Phrygian (弗里吉亚)", value: "Phrygian" },
        { label: "Lydian (利地亚)", value: "Lydian" },
        { label: "Mixolydian (混合利地亚)", value: "Mixolydian" },
        { label: "Aeolian (爱奥利亚)", value: "Aeolian" },
        { label: "Locrian (洛克利亚)", value: "Locrian" }
      ]
    },
    {
      label: "Chinese Ethnic Modes (中国民族调式)",
      children: [
        {
          label: "Chinese Pentatonic Scale (中国五声调式)",
          children: [
            { label: "宫调", value: "宫调" },
            { label: "商调", value: "商调" },
            { label: "角调", value: "角调" },
            { label: "徵调", value: "徵调" },
            { label: "羽调", value: "羽调" }
          ]
        },
        {
          label: "Chinese Hexatonic Scale (中国六声调式)",
          children: [
            { label: "清角", value: "清角" },
            { label: "变徵", value: "变徵" },
            { label: "变宫", value: "变宫" },
            { label: "闰", value: "闰" }
          ]
        },
        {
          label: "Chinese Heptatonic Scale (中国七声调式)",
          children: [
            { label: "清乐", value: "清乐" },
            { label: "雅乐", value: "雅乐" },
            { label: "燕乐", value: "燕乐" }
          ]
        }
      ]
    }
  ];
  
  // Menu handling functions
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSubmenuAnchorEl({});
    setOpenSubmenus({});
  };

  const handleSubmenuOpen = (event, key) => {
    setSubmenuAnchorEl((prev) => ({ ...prev, [key]: event.currentTarget }));
    setOpenSubmenus((prev) => ({ ...prev, [key]: true }));
  };

  const handleSubmenuClose = (key) => {
    setOpenSubmenus((prev) => ({ ...prev, [key]: false }));
    setSubmenuAnchorEl((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };
  
  // Handle mode selection
  const handleModeSelect = (selectedMode) => {
    onChange({ ...value, mode: selectedMode });
    handleCloseMenu();
  };
  
  // Handle note change
  const handleNoteChange = (newNote) => {
    onChange({ ...value, ...newNote });
  };
  
  // Recursive function to render menu items
  const renderMenuItems = (items, parentKey = "") =>
    items.map((item) => {
      const key = `${parentKey}-${item.label}`;

      return (
        <div key={key} style={{ position: "relative" }}>
          {item.children ? (
            <>
              <MenuItem
                onMouseEnter={(e) => handleSubmenuOpen(e, key)}
                onMouseLeave={() => {
                  setTimeout(() => {
                    if (!openSubmenus[key]) handleSubmenuClose(key);
                  }, 100);
                }}
                onClick={(e) => handleSubmenuOpen(e, key)}
              >
                {item.label}
              </MenuItem>
              <Menu
                anchorEl={
                  submenuAnchorEl[key] && document.contains(submenuAnchorEl[key])
                    ? submenuAnchorEl[key]
                    : null
                }
                open={Boolean(submenuAnchorEl[key])}
                onClose={() => handleSubmenuClose(key)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                disableAutoFocusItem
                MenuListProps={{
                  onMouseEnter: () => setOpenSubmenus((prev) => ({ ...prev, [key]: true })),
                  onMouseLeave: () => handleSubmenuClose(key),
                }}
              >
                {renderMenuItems(item.children, key)}
              </Menu>
            </>
          ) : (
            <MenuItem onClick={() => handleModeSelect(item.value)}>
              {item.label}
            </MenuItem>
          )}
        </div>
      );
    });
  
  // Get display label for the current mode
  const getModeDisplayLabel = () => {
    // Helper function to search through the hierarchical structure
    const findModeLabel = (items) => {
      for (const item of items) {
        if (item.value === value.mode) {
          return item.label;
        }
        if (item.children) {
          const found = findModeLabel(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    const label = findModeLabel(modeItems);
    return label || value.mode;
  };
  
  return (
    <div className="space-y-4">
      <MusicNoteSelector 
        value={{ note: value.note, accidental: value.accidental }} 
        onChange={handleNoteChange} 
      />
      
      <div>
        <label className="text-sm font-medium block mb-1" style={{ color: COLORS.TEXT_SECONDARY }}>Mode</label>
        <Button
          onClick={handleOpenMenu}
          variant="outlined"
          className="w-full justify-between normal-case"
          style={{ 
            textTransform: 'none', 
            justifyContent: 'space-between',
            padding: '0.25rem 0.5rem',
            border: '1px solid #d1d5db',
            color: 'black',
            backgroundColor: 'white',
            minWidth: '200px'
          }}
        >
          {value.mode ? getModeDisplayLabel() : "Select Mode"}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          MenuListProps={{
            onMouseLeave: handleCloseMenu,
          }}
        >
          {renderMenuItems(modeItems)}
        </Menu>
      </div>
      
      <div className="mt-3">
        <span className="text-lg font-medium">
          Selected Key: {value.note}{value.accidental} {value.mode}
        </span>
      </div>
    </div>
  );
};

export default KeySelector;
